'use client';

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountError, useBlockPart, useCommerceSource, tx, PortalLink } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  AccountField,
  NEW_PASSWORD_MIN_LENGTH,
  buttonStyle,
  errorStyle,
  formStyle,
  leadStyle,
  linkRowStyle,
  mutedStyle,
  noticeStyle,
  secondaryButtonStyle,
  titleStyle,
  useAccountErrorText,
  useQueryParam,
} from './accountShared';
import { afterSignUpHref, newPasswordProblem, signUp } from './registration';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Create an account.
 *
 * <p>Name, email and a password, typed twice, and no more. Every extra field on a sign-up form
 * costs sign-ups, and nothing else is needed to buy: addresses are asked for at checkout, where
 * the shopper can see why.
 *
 * <p>As in most online shops, the account opens at once: the shopper is signed in straight away
 * and taken on, to `?next=` when that is a path on this site, else to `successHref`, and the shop
 * emails a welcome. The password is checked against the shop's rule before it is sent, with the
 * same words the shop would use to refuse it.
 *
 * <p>A shop set up to confirm new addresses first answers differently: it emails a link and a
 * code, and the form says so; the account is ready on the page the link opens
 * ({@link ActivateAccountForm}).
 *
 * <p>An address that already has an account is told so, with the ways back in: signing in, a
 * forgotten password, or the email sent again for an account that was never confirmed.
 */
export interface RegisterFormProps {
  title?: Localized;
  intro?: Localized;
  /** Where the shopper goes, signed in, once the account is open, unless `?next=` names a page on this site. */
  successHref?: string;
  /** The page a confirmation email's link opens, where the code is entered. */
  activateHref?: string;
  signInHref?: string;
  forgotHref?: string;
  termsHref?: string;
}

export function RegisterForm({
  title,
  intro,
  successHref = '/account',
  activateHref = '/activate-account',
  signInHref = '/sign-in',
  forgotHref = '/forgot-password',
  termsHref = '/terms',
}: RegisterFormProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();
  const next = useQueryParam('next');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  /** The address the confirmation email went to, once it has gone. */
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [taken, setTaken] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!commerce?.register) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="RegisterForm"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce?.register || busy) return;
    setTaken(false);
    setResent(false);
    const problem = newPasswordProblem(password, confirmPassword);
    if (problem) {
      setError(t(`commerce.account.errors.${problem}`));
      return;
    }
    // Shown back, and asked for again, as the account stores it.
    const address = email.trim().toLowerCase();
    setBusy(true);
    setError(null);
    try {
      const outcome = await signUp(commerce, {
        email: address,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        confirmPassword,
      });
      const href = afterSignUpHref(outcome, { next: next.value, successHref, signInHref });
      if (href) {
        // A full load, as after signing in, so every block re-reads who is signed in.
        window.location.assign(href);
        return;
      }
      setSentTo(address);
    } catch (failure) {
      setTaken(failure instanceof AccountError && failure.kind === 'EMAIL_TAKEN');
      setError(errorText(failure));
    }
    setBusy(false);
  }

  async function resend(address: string) {
    if (!commerce?.resendActivation || busy) return;
    setBusy(true);
    setError(null);
    try {
      await commerce.resendActivation(address);
      setResent(true);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  const resendButton = (address: string) =>
    commerce.resendActivation && !resent ? (
      <button type="button" disabled={busy} onClick={() => void resend(address)} style={secondaryButtonStyle}>
        {busy ? t('commerce.account.sending') : t('commerce.account.resendActivation')}
      </button>
    ) : null;

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 520 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.account.registerTitle')}
        </h1>
        {sentTo ? (
          <>
            <p role="status" style={noticeStyle}>
              {t('commerce.account.registerSent', { email: sentTo })}
            </p>
            {resent && (
              <p role="status" style={{ ...mutedStyle, marginTop: 12 }}>
                {t('commerce.account.activationResent')}
              </p>
            )}
            {error && (
              <p role="alert" style={{ ...errorStyle, marginTop: 12 }}>
                {error}
              </p>
            )}
            <div style={{ ...linkRowStyle, alignItems: 'center' }}>
              <PortalLink href={activateHref}>{t('commerce.account.enterActivationCode')}</PortalLink>
              {resendButton(sentTo)}
            </div>
          </>
        ) : (
          <>
            <p {...part('intro')} style={leadStyle}>
              {tx(intro) || t('commerce.account.registerIntro')}
            </p>
            <form onSubmit={(event) => void submit(event)} style={formStyle}>
              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
              {resent && (
                <p role="status" style={noticeStyle}>
                  {t('commerce.account.activationResent')}
                </p>
              )}
              {taken && (
                <div style={{ ...linkRowStyle, marginTop: 0, alignItems: 'center' }}>
                  <PortalLink href={signInHref}>{t('commerce.account.signIn')}</PortalLink>
                  <PortalLink href={forgotHref}>{t('commerce.account.forgotLink')}</PortalLink>
                  {email.trim() && resendButton(email.trim())}
                </div>
              )}
              <AccountField
                id="register-first-name"
                label={t('commerce.account.firstName')}
                value={firstName}
                onChange={setFirstName}
                autoComplete="given-name"
                required
                maxLength={100}
              />
              <AccountField
                id="register-last-name"
                label={t('commerce.account.lastName')}
                value={lastName}
                onChange={setLastName}
                autoComplete="family-name"
                required
                maxLength={100}
              />
              <AccountField
                id="register-email"
                label={t('commerce.account.email')}
                value={email}
                onChange={setEmail}
                type="email"
                autoComplete="email"
                required
                maxLength={254}
              />
              <AccountField
                id="register-password"
                label={t('commerce.account.password')}
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="new-password"
                required
                maxLength={1000}
                hint={t('commerce.account.passwordRuleHint', { count: NEW_PASSWORD_MIN_LENGTH })}
              />
              <AccountField
                id="register-confirm-password"
                label={t('commerce.account.confirmPassword')}
                value={confirmPassword}
                onChange={setConfirmPassword}
                type="password"
                autoComplete="new-password"
                required
                maxLength={1000}
              />
              <p style={{ ...mutedStyle, fontSize: 13 }}>
                {t('commerce.account.termsNotice')}{' '}
                <PortalLink href={termsHref}>{t('commerce.account.termsLink')}</PortalLink>
              </p>
              <button type="submit" disabled={busy} style={buttonStyle}>
                {busy ? t('commerce.account.creating') : t('commerce.account.createAccount')}
              </button>
            </form>
            <div style={linkRowStyle}>
              <span style={mutedStyle}>
                {t('commerce.account.haveAccount')}{' '}
                <PortalLink href={signInHref}>{t('commerce.account.signIn')}</PortalLink>
              </span>
            </div>
          </>
        )}
      </div>
    </BlockRoot>
  );
}
